
import { useEffect } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import HeroSection from './components/HeroSection';
import PageTransition from '../../components/feature/PageTransition';

const HomePage = () => {
  useEffect(() => {
    const schema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          "@id": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#organization`,
          "name": "삼원테크",
          "url": import.meta.env.VITE_SITE_URL || 'https://example.com',
          "logo": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/logo.png`,
          "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+82-61-373-3221",
            "contactType": "customer service",
            "areaServed": "KR",
            "availableLanguage": "Korean"
          },
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "동농공길 26-3",
            "addressLocality": "화순군 동면",
            "addressRegion": "전라남도",
            "postalCode": "58126",
            "addressCountry": "KR"
          },
          "sameAs": []
        },
        {
          "@type": "LocalBusiness",
          "@id": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#localbusiness`,
          "name": "삼원테크",
          "description": "30년 전통의 스테인리스 물탱크 전문 제조업체. STS패널형·원통형·라이닝 물탱크 직접 생산 및 시공.",
          "url": import.meta.env.VITE_SITE_URL || 'https://example.com',
          "telephone": "+82-61-373-3221",
          "email": "samwontk@hanmail.net",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "동농공길 26-3",
            "addressLocality": "화순군 동면",
            "addressRegion": "전라남도",
            "postalCode": "58126",
            "addressCountry": "KR"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": 35.0658,
            "longitude": 126.9869
          },
          "openingHoursSpecification": [
            {
              "@type": "OpeningHoursSpecification",
              "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"],
              "opens": "08:00",
              "closes": "18:00"
            }
          ],
          "priceRange": "$$"
        },
        {
          "@type": "WebSite",
          "@id": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#website`,
          "url": import.meta.env.VITE_SITE_URL || 'https://example.com',
          "name": "삼원테크",
          "inLanguage": "ko-KR"
        }
      ]
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-home';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { document.getElementById('schema-home')?.remove(); };
  }, []);

  useEffect(() => {
    document.title = '삼원테크 | 전남 화순 스테인리스 물탱크 전문 제조업체 STS패널형·원통형·라이닝';
    document.querySelector('meta[name="description"]')?.setAttribute('content', '삼원테크는 전남 화순 소재 30년 전통의 스테인리스 물탱크 전문 제조업체입니다. STS패널형(3~250톤), STS원통형(3~500톤), 라이닝 물탱크를 직접 생산·시공하며 ISO9001·ISO14001·KS 인증을 보유하고 있습니다.');
  }, []);

  return (
    <PageTransition>
      <div className="min-h-screen bg-white">
        <Navigation />
        <main>
          <HeroSection />
          
          {/* Combined Vision + CEO Message Section with shared background */}
          <section id="vision-ceo" className="relative py-24 px-6 overflow-hidden">
            {/* Shared Background Image */}
            <div className="absolute inset-0">
              <img
                src="https://public.readdy.ai/ai/img_res/f6813e7d365917df3d4a1aa3ed24205d.jpg"
                alt="배경"
                className="w-full h-full object-cover object-top"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-white/70"></div>
            </div>

            {/* Vision Content */}
            <div className="relative max-w-4xl mx-auto text-center mb-28">
              <h2 className="text-4xl font-bold text-gray-900 mb-8">
                삼원테크의 비전과 가치
              </h2>
              <p className="text-xl text-gray-700 leading-relaxed">
                <span className="text-emerald-600 font-semibold">환경과 고객</span>을 제일 먼저 생각하며<br />
                축적된 기술과 경험을 바탕으로 맑은 물을<br />
                담아 전해드리기 위해 <span className="text-emerald-600 font-semibold">책임</span>을 다합니다.
              </p>
            </div>

            {/* CEO Message Content */}
            <div className="relative max-w-6xl mx-auto">
              <div className="flex items-center justify-center mb-10">
                <span className="inline-flex items-center gap-2 text-emerald-600 text-sm font-semibold tracking-widest uppercase">
                  <span className="w-8 h-px bg-emerald-500"></span>
                  대표님이 전하는 말
                  <span className="w-8 h-px bg-emerald-500"></span>
                </span>
              </div>

              <div className="grid lg:grid-cols-5 gap-12 items-start">
                <div className="lg:col-span-3 flex justify-start -ml-16 -my-16">
                  <div className="relative w-[75%] h-[680px]">
                    <img
                      src="https://public.readdy.ai/ai/img_res/99715718-7214-4f84-8d9e-eb979072457f.png"
                      alt="삼원테크 대표이사"
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                <div className="lg:col-span-2">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 flex items-center justify-center bg-emerald-50 rounded-full border border-emerald-200">
                      <img
                        src="https://static.readdy.ai/image/75975fdd6a8419c14194c00fe1023b5f/06580d5e232d5cb0cebd682b84a80f70.png"
                        alt="삼원테크 로고"
                        className="w-8 h-8 object-contain"
                      />
                    </div>
                    <i className="ri-double-quotes-l text-4xl text-emerald-400/60"></i>
                  </div>

                  <div className="space-y-5">
                    <p className="text-gray-700 text-base leading-[2] font-light">
                      우리 겨레는 예로부터 먹거리에 있어서 가장 기본으로 생각한 것은 <strong className="text-emerald-600 font-semibold">물</strong>이었습니다.
                      지구상의 생명체는 모두 삶의 원천을 물에 의존하고 있고, 특히 사람은 어머니의 양수에서 조직이 형성되어
                      인간의 모습을 갖추게 되는 만큼 <strong className="text-emerald-600 font-semibold">물은 생명활동의 중추</strong>로써 우리 몸의 70%~80%를 구성하고 있습니다.
                    </p>
                    <p className="text-gray-700 text-base leading-[2] font-light">
                      매년 장마철이면 홍수로 인해 인적 물적 피해가 발생하면서도 아이러니하게도 우리나라는
                      UN이 정한 <strong className="text-emerald-600 font-semibold">물부족 국가</strong>로 분류되어 있습니다.
                      또한 오늘날 식수는 환경오염 등으로 인해 신선한 물을 안심하고 마음껏 마실 수도 없게 되었습니다.
                    </p>
                    <p className="text-gray-700 text-base leading-[2] font-light">
                      이러한 물의 중요성을 일찍이 인식하여 가뭄에 대비해 <strong className="text-emerald-600 font-semibold">청결하고 안정적인 물</strong>을
                      지속적으로 공급하고자 노력해왔습니다.
                      앞으로도 지속적인 연구개발과 항상 최선을 다하는 자세로 고객님들께 무한한 감동을 줄 수 있도록
                      전 임직원이 노력하겠습니다.
                    </p>
                  </div>

                  <div className="text-right mt-4">
                    <i className="ri-double-quotes-r text-4xl text-emerald-400/60"></i>
                  </div>

                  <div className="mt-6 flex flex-col items-end gap-2">
                    <div className="w-16 h-px bg-emerald-400/40"></div>
                    <p className="text-gray-500 text-sm tracking-widest mt-2">삼원테크 대표이사</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

        </main>
        <Footer />
      </div>
    </PageTransition>
  );
};

export default HomePage;
